import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { LOCALE_STORAGE_KEY, SUPPORTED_LOCALES } from "@/lib/locales";
import { useAuth } from "@/lib/auth";
import { useUpdateUiLocale } from "@/lib/flashcards";

interface Props {
  className?: string;
  // "default" = full-width trigger showing the native language name (footer,
  // settings). "compact" = a small globe + 2-letter code, for the navbar.
  variant?: "default" | "compact";
}

// The find-a-tutor / become-a-tutor pages render Sanity content keyed off the
// URL locale prefix (/fr/find-a-tutor/spanish), so switching language on one of
// them must rewrite the URL — i18next alone only changes the surrounding chrome.
// The rest of the app is pure client-side i18next and needs no navigation.
const LOCALE_PAGE = /^\/(?:[a-z]{2}\/)?(find-a-tutor|become-a-tutor)\/([^/?#]+)/;
function localePagePath(pathname: string, code: string): string | null {
  const m = pathname.match(LOCALE_PAGE);
  if (!m) return null;
  const [, section, subject] = m;
  return code === "en" ? `/${section}/${subject}` : `/${code}/${section}/${subject}`;
}

// A self-contained language picker (native names). Persists the choice to
// localStorage for everyone, and — when signed in — to the user's `ui_locale`
// so it follows them across devices. Drop it anywhere (navbar, footer, settings).
export default function LanguageSwitcher({ className, variant = "default" }: Props) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const updateUiLocale = useUpdateUiLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const current = i18n.language;

  function change(code: string) {
    i18n.changeLanguage(code);
    if (typeof window !== "undefined") window.localStorage.setItem(LOCALE_STORAGE_KEY, code);
    if (isAuthenticated) updateUiLocale.mutate(code);
    // On a URL-locale-driven content page, follow the language into the URL so
    // the page body (not just the chrome) switches too.
    const target = localePagePath(location.pathname, code);
    if (target && target !== location.pathname) navigate(target);
  }

  // Each language's name is written in that language ("Español", "中文"), so a
  // browser translating the page must leave it alone — in the list, and in the
  // trigger, where Radix shows a copy of the chosen item. Only here: other
  // Selects stay translatable for people whose language Jynio does not offer.
  const options = (
    <SelectContent align="end">
      {SUPPORTED_LOCALES.map((l) => (
        <SelectItem key={l.code} value={l.code}>
          <span translate="no">{l.native}</span>
        </SelectItem>
      ))}
    </SelectContent>
  );

  if (variant === "compact") {
    return (
      <Select value={current} onValueChange={change}>
        <SelectTrigger className={cn("h-11 md:h-9 w-auto gap-1.5 px-2.5", className)} aria-label={t("language.label")}>
          <Globe className="w-4 h-4 shrink-0 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase">{current.slice(0, 2)}</span>
        </SelectTrigger>
        {options}
      </Select>
    );
  }

  return (
    <Select value={current} onValueChange={change}>
      <SelectTrigger className={className} aria-label={t("language.label")}>
        <Globe className="w-4 h-4 mr-2 shrink-0 text-muted-foreground" />
        <SelectValue />
      </SelectTrigger>
      {options}
    </Select>
  );
}
